import { useEffect, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import "./DataFlowVisualization.css";

interface DataRateHistogramProps {
  data: any[]; // Array of data items with timestamps
  height?: number; // Chart height (default: 150)
  width?: number; // Chart width (default: 900)
  maxBars?: number; // Maximum number of bars to show (default: 60 for 60 seconds)
  getTimestamp?: (item: any) => number; // Function to extract timestamp
}

/**
 * Histogram component showing data rate (data points per second)
 * X-axis: Time (seconds)
 * Y-axis: Number of data rows per second
 */
export default function DataRateHistogram({
  data,
  height = 150,
  width = 900,
  maxBars = 60, // Show last 60 seconds
  getTimestamp = (item) => item.timestamp || Date.now(),
}: DataRateHistogramProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<d3.Selection<HTMLDivElement, unknown, HTMLElement, any> | null>(null);
  const [rateData, setRateData] = useState<{ time: number; count: number }[]>([]);

  // Memoize getTimestamp to avoid unnecessary recalculations
  const getTimestampMemo = useCallback(
    (item: any) => getTimestamp(item),
    [getTimestamp]
  );

  // Calculate data rate per second
  useEffect(() => {
    if (data.length === 0) {
      setRateData([]);
      return;
    }

    const now = Date.now();
    const timeMap = new Map<number, number>();

    // Group data by second (using timestamp from data)
    data.forEach((item) => {
      const timestamp = getTimestampMemo(item);
      // Validate timestamp (must be a valid number)
      if (!timestamp || isNaN(timestamp) || !isFinite(timestamp)) {
        return;
      }
      
      // Calculate which second bucket this data point belongs to
      const secondsAgo = Math.floor((now - timestamp) / 1000);
      
      // Only track recent seconds (within maxBars range)
      // Handle both past and future timestamps gracefully
      if (secondsAgo >= 0 && secondsAgo < maxBars) {
        const count = timeMap.get(secondsAgo) || 0;
        timeMap.set(secondsAgo, count + 1);
      }
    });

    // Create array for all seconds in the range (oldest to newest)
    const rates: { time: number; count: number }[] = [];
    for (let i = maxBars - 1; i >= 0; i--) {
      rates.push({
        time: i, // seconds ago
        count: timeMap.get(i) || 0,
      });
    }

    setRateData(rates);
  }, [data, maxBars, getTimestampMemo]);

  useEffect(() => {
    if (!svgRef.current || rateData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const margin = { top: 20, right: 20, bottom: 40, left: 50 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const chart = svg
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    // Scales
    const xScale = d3
      .scaleBand()
      .domain(rateData.map((_, i) => i.toString()))
      .range([0, chartWidth])
      .padding(0.1);

    // Set maximum y-axis value to 5
    const yAxisMax = 5;
    const yScale = d3
      .scaleLinear()
      .domain([0, yAxisMax])
      .range([chartHeight, 0])
      .nice();

    // X-axis
    chart
      .append("g")
      .attr("transform", `translate(0, ${chartHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues(
            xScale.domain().filter((_, i) => i % Math.ceil(maxBars / 10) === 0)
          )
          .tickFormat((d) => {
            const index = parseInt(d || "0");
            const secondsAgo = rateData[index]?.time || 0;
            if (secondsAgo === 0) return "now";
            if (secondsAgo < 60) return `-${secondsAgo}s`;
            const minutes = Math.floor(secondsAgo / 60);
            return `-${minutes}m`;
          })
      )
      .selectAll("text")
      .attr("class", "data-rate-axis-text");

    // Y-axis
    chart
      .append("g")
      .call(d3.axisLeft(yScale).ticks(5))
      .selectAll("text")
      .attr("class", "data-rate-axis-text");

    // Y-axis label
    chart
      .append("text")
      .attr("class", "data-rate-y-axis-label")
      .attr("transform", "rotate(-90)")
      .attr("y", -margin.left + 15)
      .attr("x", -chartHeight / 2)
      .attr("text-anchor", "middle")
      .text("Data Points/sec");

    // Bars
    chart
      .selectAll("rect")
      .data(rateData)
      .enter()
      .append("rect")
      .attr("x", (_, i) => xScale(i.toString()) || 0)
      .attr("y", (d) => yScale(d.count))
      .attr("width", xScale.bandwidth())
      .attr("height", (d) => chartHeight - yScale(d.count))
      .attr("fill", (d) => {
        // Color based on count relative to y-axis max (5)
        // Green for high (>= 3.5), Orange for medium (>= 2), Red for low (> 0), Gray for zero
        if (d.count >= 3.5) return "#4CAF50"; // Green
        if (d.count >= 2) return "#FF9800"; // Orange
        if (d.count > 0) return "#F44336"; // Red
        return "#E0E0E0"; // Gray for zero
      })
      .attr("rx", 2)
      .style("opacity", 0.8)
      .on("mouseover", function (event, d) {
        const rect = this;
        d3.select(rect).style("opacity", 1);
        
        // Remove any existing tooltip
        if (tooltipRef.current) {
          tooltipRef.current.remove();
        }
        
        // Show tooltip
        const tooltip = d3
          .select("body")
          .append("div")
          .attr("class", "data-rate-tooltip")
          .style("opacity", 0);

        // Get page coordinates for tooltip positioning
        const svgRect = svgRef.current?.getBoundingClientRect();
        const [x, y] = d3.pointer(event, svgRef.current);
        
        if (svgRect) {
          tooltip
            .style("left", `${svgRect.left + x + 10}px`)
            .style("top", `${svgRect.top + y - 10}px`)
            .text(`${d.count} data points ${d.time === 0 ? "now" : `${d.time}s ago`}`)
            .transition()
            .duration(200)
            .style("opacity", 1);
          
          // Only assign tooltip reference if it was successfully positioned
          tooltipRef.current = tooltip;
        } else {
          // If positioning failed, remove the tooltip immediately
          tooltip.remove();
        }
      })
      .on("mouseout", function () {
        d3.select(this).style("opacity", 0.8);
        if (tooltipRef.current) {
          tooltipRef.current
            .transition()
            .duration(200)
            .style("opacity", 0)
            .remove();
          tooltipRef.current = null;
        }
      });

    // Grid lines
    chart
      .append("g")
      .attr("class", "grid")
      .attr("transform", `translate(0, ${chartHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .tickSize(-chartHeight)
          .tickValues(
            xScale.domain().filter((_, i) => i % Math.ceil(maxBars / 10) === 0)
          )
          .tickFormat(() => "")
      )
      .selectAll("line")
      .attr("class", "data-rate-grid-line");

    // Cleanup function to remove tooltip on unmount or data change
    return () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
      // Also remove any tooltips that might be left in the DOM
      d3.selectAll(".data-rate-tooltip").remove();
    };
  }, [rateData, width, height, maxBars]);

  return (
    <div className="data-rate-histogram-container">
      <h4 className="data-rate-histogram-title">
        Data Rate (points/second)
      </h4>
      <svg
        ref={svgRef}
        className="data-rate-histogram-svg"
        width={width}
        height={height}
      />
    </div>
  );
}
